// Learner model: evidence per concept and per link, never a single "understood" flag.
import { z } from 'zod';
import { CompetenceState } from './practice.ts';

export const Evidence = z.object({
  /** Concept id or link id ("from>to"). */
  target: z.string(),
  state: CompetenceState,
  ok: z.boolean(),
  /** 'tutor' = reported in a tutor turn; 'review' = outcome of a scheduled redraw; 'guess' = inline lesson guess. */
  source: z.enum(['tutor', 'review', 'guess']),
  attemptId: z.string().nullable(),
  at: z.string(),
});

/** Derived per target from evidence: the latest result in each state. */
export const TargetState = z.object({
  target: z.string(),
  label: z.string(),
  kind: z.enum(['concept', 'link']),
  states: z.partialRecord(CompetenceState, z.object({ ok: z.boolean(), at: z.string() })),
  /** Wrong at first, right later: the "could not → can now" list. */
  improved: z.boolean(),
  /** Open misconceptions seen on this target. */
  misconceptions: z.array(z.string()),
});

/** A scheduled blank redraw. Intervals: 3 days, then 10, then 30; a miss resets to 3. */
export const Review = z.object({
  id: z.string(),
  lessonId: z.string(),
  checkpointId: z.string(),
  dueAt: z.string(),
  intervalDays: z.number().int().min(1),
  lastResult: z.enum(['correct', 'partial', 'open']).nullable(),
  /** Why it was scheduled, shown to the learner ("other tokens' keys and values came back once"). */
  reason: z.string().max(200),
});

export type Evidence = z.infer<typeof Evidence>;
export type TargetState = z.infer<typeof TargetState>;
export type Review = z.infer<typeof Review>;
